/// Port of mobile/lib/features/auth/ui/reset_password_screen.dart.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import * as authRepository from '@/auth/data/authRepository';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelActionButton } from '@/ui/pixel/PixelActionButton';
import { PixelPasswordField } from '@/ui/pixel/PixelPasswordField';

export function ResetPasswordScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (): Promise<void> => {
    if (!token) {
      setError(t('authResetMissingToken'));
      return;
    }
    if (password.length < 8) {
      setError(t('authResetPasswordTooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('authResetPasswordMismatch'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await authRepository.resetPassword(token, password);
      setDone(true);
    } catch {
      setError(t('authResetFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageChrome
      title={t('authResetPasswordTitle')}
      onBack={done ? () => navigate(appRoutes.home) : undefined}
    >
      <div className="flex-1 overflow-y-auto px-6 pb-10 pt-5">
        {done ? (
          <>
            <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
              {t('authResetSuccess')}
            </p>
            <PixelActionButton
              label={t('authSignIn')}
              className="mt-6"
              onClick={() => navigate(appRoutes.auth)}
            />
          </>
        ) : (
          <>
            <p
              className="leading-tight"
              style={{ fontSize: 16, color: colors.onSurface, opacity: 0.75 }}
            >
              {t('authResetPasswordHint')}
            </p>

            <div className="mt-5 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              {t('authPassword')}
            </div>
            <div className="mt-2">
              <PixelPasswordField
                value={password}
                onChange={(value) => {
                  setPassword(value);
                  if (error) setError(null);
                }}
                autoComplete="new-password"
                disabled={busy}
              />
            </div>

            <div className="mt-4 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              {t('authResetConfirmPassword')}
            </div>
            <div className="mt-2">
              <PixelPasswordField
                value={confirm}
                onChange={(value) => {
                  setConfirm(value);
                  if (error) setError(null);
                }}
                autoComplete="new-password"
                disabled={busy}
              />
            </div>

            {error && (
              <p className="mt-3 leading-tight" style={{ fontSize: 16, color: '#ef5350' }}>
                {error}
              </p>
            )}

            <PixelActionButton
              label={t('authResetSubmit')}
              busy={busy}
              className="mt-6"
              onClick={busy ? undefined : () => void onSubmit()}
            />
          </>
        )}
      </div>
    </PageChrome>
  );
}
